import { Immutable } from "immer";
import React from "react";
import { PLCConfigurator } from "tsi-common-react/src/apps/configurator/PLCConfigurator/index";
import {
    UpsellBlockValue,
    UpsellModal,
} from "tsi-common-react/src/apps/configurator/UpsellModal";
import { IUpsellModalComponentFn } from "tsi-common-react/src/apps/configurator/models";
import { IProduct } from "tsi-common-react/src/models/catalogue";
import {
    IProductID,
    isoProductID,
    isoSafeHTML,
    isoWebPageURL,
} from "tsi-common-react/src/models/nominals";
import { check } from "tsi-common-react/src/models/utils";

import { reverse } from "@reactivated";
import type { templates } from "@reactivated";

import * as pdpModels from "./models";

type IProps = Immutable<{
    configurator: templates.ProductLineCollectionPageTemplate["plc_configurator"];
    cmsJson: string;
}>;

// Decode the page's serialized CMS payload (props.cms_json) into the PDP CMS
// model. Previously this was JSON.parsed off #product-detail-container[data-cms]
// at module scope in apps/product-detail/index.tsx; it now happens client-side
// here (this module loads only via PLCConfiguratorApp's React.lazy import), fed
// by the reactivated document prop rather than the DOM attribute.
const decodeCmsData = (cmsJson: string): pdpModels.IProductPage | null => {
    if (!cmsJson) {
        return null;
    }
    // Tolerant decode: the ProductPage codec models the PDP page types, but
    // ProductLineCollectionPage lacks some of their fields (e.g.
    // show_financing_modal), so real PLC payloads can fail the strict check.
    // Degrade to "no CMS extras" (no upsell modal, no shipping lead) instead
    // of taking down the whole configurator.
    try {
        return check(pdpModels.ProductPage.decode(JSON.parse(cmsJson)));
    } catch (e) {
        console.warn("PLC cms_json payload failed to decode:", e);
        return null;
    }
};

// The ErrorBoundary lives in PLCConfiguratorApp (wrapping Suspense) so it also
// catches chunk-load failures of this lazy module. The reactivated renderer +
// the client entry already wrap the document in the Redux Provider, so no
// Provider is needed here.
const PLCConfiguratorAppInner = (props: IProps): React.JSX.Element => {
    const c = props.configurator;
    const cmsData = React.useMemo(
        () => decodeCmsData(props.cmsJson),
        [props.cmsJson],
    );
    const pdpRootProductDescriptions =
        cmsData?.variant_configurator_description_data ?? [];
    const initialVariant = isoProductID.wrap(
        parseInt(c.initial_variant || "", 10),
    );
    const defaultVariant = isoProductID.wrap(
        parseInt(c.default_variant || "", 10),
    );
    const preorderShippingDescriptions = new Map<IProductID, string>(
        (pdpRootProductDescriptions || [])
            .filter((d) => !!d.preorder_shipping_description)
            .map((d) => [d.product, d.preorder_shipping_description!]),
    );
    return (
        <PLCConfigurator
            preheader={c.preheader || ""}
            title={c.title || ""}
            subhead={c.subhead || ""}
            description={c.description || ""}
            footer={isoSafeHTML.wrap(c.footer_html || "")}
            categorySelectors={c.category_selectors_json || "[]"}
            products={c.products_json || "[]"}
            optionPanels={c.option_panels_json || "[]"}
            initialVariantID={initialVariant || defaultVariant}
            enableHistoryInteraction={true}
            starRatingURL={isoWebPageURL.wrap("#reviews")}
            basketLink={isoWebPageURL.wrap(reverse("basket:summary"))}
            configureGiftsLink={isoWebPageURL.wrap(
                reverse("basket:configure-gifts"),
            )}
            showUpsellModal={() => {
                return (
                    !!cmsData &&
                    !!cmsData.bundle_modal_serialized &&
                    cmsData.bundle_modal_serialized.length > 0
                );
            }}
            getUpsellModalComponentFn={(): IUpsellModalComponentFn | null => {
                const serialized = cmsData?.bundle_modal_serialized;
                if (!serialized || serialized.length === 0) {
                    return null;
                }
                // Temporary: cast io-ts-decoded PLC data to Reactivated shape. Remove when PLC path migrates to SSR.
                const blockData = serialized[0] as UpsellBlockValue;
                return (modalProps) => (
                    <UpsellModal {...modalProps} blockData={blockData} />
                );
            }}
            showShippingLead={!!(cmsData && cmsData.show_shipping_lead)}
            phoneNumber={c.phone_number}
            getDeliveryCopy={(rootProduct: IProduct, isPreorder: boolean) => {
                if (isPreorder) {
                    return (
                        preorderShippingDescriptions.get(rootProduct.id) || ""
                    );
                }
                return cmsData && cmsData.shipping_description
                    ? cmsData.shipping_description
                    : "";
            }}
            getDeliveryIcon={() => {
                return cmsData && cmsData.shipping_icon_url
                    ? cmsData.shipping_icon_url
                    : null;
            }}
        />
    );
};

export default PLCConfiguratorAppInner;
